import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { financeApi, FundingSource } from '../../api/finance';
import { masterDataApi } from '../../api/masterData';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { useToast } from '../../components/Toast';

const SOURCE_TYPE_LABELS: Record<string, string> = { donor: 'Donor', government: 'Government', un_agency: 'UN Agency', foundation: 'Foundation', private_donor: 'Private Donor', other: 'Other' };
const FUND_TYPE_LABELS: Record<string, string> = { restricted: 'Restricted', unrestricted: 'Unrestricted', designated: 'Designated' };

export function FundingSourcesPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const fundingSourcesQuery = useQuery({ queryKey: ['finance-funding-sources'], queryFn: () => financeApi.listFundingSources() });
  const donorsQuery = useQuery({ queryKey: ['master-donors'], queryFn: () => masterDataApi.donors(), enabled: showForm });
  const grantsQuery = useQuery({ queryKey: ['finance-grants'], queryFn: () => financeApi.listGrants(), enabled: showForm });

  const { register, handleSubmit, reset } = useForm<Record<string, string>>();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['finance-funding-sources'] });

  const createMutation = useMutation({
    mutationFn: (v: Record<string, string>) => financeApi.createFundingSource({
      name: v.name, sourceType: v.sourceType, donorId: v.donorId ? Number(v.donorId) : undefined, grantId: v.grantId ? Number(v.grantId) : undefined,
      currencyCode: v.currencyCode || 'NGN', startDate: v.startDate || undefined, endDate: v.endDate || undefined, fundType: v.fundType,
    }),
    onSuccess: () => { showSuccess('Funding source created.'); invalidate(); setShowForm(false); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create funding source.'),
  });

  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'active' | 'inactive' }) => financeApi.setFundingSourceStatus(id, status),
    onSuccess: () => { showSuccess('Updated.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  return (
    <div className="page">
      <div className="page-header">
        <h1>Funding Sources</h1>
        <button onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : 'Create Funding Source'}</button>
      </div>

      {showForm && (
        <section className="card">
          <h2>New Funding Source</h2>
          <form onSubmit={handleSubmit((v) => createMutation.mutate(v))}>
            <label>Name<input {...register('name', { required: true })} /></label>
            <label>
              Type
              <select {...register('sourceType', { required: true })}>
                <option value="">- select -</option>
                {Object.entries(SOURCE_TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
            <label>
              Donor
              <select {...register('donorId')}>
                <option value="">- select -</option>
                {donorsQuery.data?.data.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </label>
            <label>
              Grant
              <select {...register('grantId')}>
                <option value="">- select -</option>
                {grantsQuery.data?.data.map((g) => <option key={g.id} value={g.id}>{g.grant_reference} - {g.grant_name}</option>)}
              </select>
            </label>
            <label>Currency<input {...register('currencyCode')} placeholder="NGN" /></label>
            <label>Start Date<input type="date" {...register('startDate')} /></label>
            <label>End Date<input type="date" {...register('endDate')} /></label>
            <label>
              Fund Type
              <select {...register('fundType', { required: true })}>
                {Object.entries(FUND_TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
            <div className="form-actions"><button type="submit" className="btn-primary" disabled={createMutation.isPending}>{createMutation.isPending ? 'Creating…' : 'Create'}</button><button type="button" className="btn-secondary" onClick={() => { setShowForm(false); reset(); }}>Cancel</button></div>
          </form>
        </section>
      )}

      <section className="card">
        {fundingSourcesQuery.data && (
          <DataTable<FundingSource>
            rows={fundingSourcesQuery.data.data}
            rowKey={(f) => f.id}
            emptyMessage="No funding sources yet."
            columns={[
              { header: 'Name', render: (f) => f.name },
              { header: 'Type', render: (f) => SOURCE_TYPE_LABELS[f.source_type] ?? f.source_type },
              { header: 'Donor', render: (f) => f.donor_name ?? '-' },
              { header: 'Fund Type', render: (f) => FUND_TYPE_LABELS[f.fund_type] ?? f.fund_type },
              { header: 'Status', render: (f) => <StatusBadge status={f.status} /> },
              { header: '', render: (f) => <button className="link-button" onClick={() => toggleStatusMutation.mutate({ id: f.id, status: f.status === 'active' ? 'inactive' : 'active' })}>{f.status === 'active' ? 'Deactivate' : 'Activate'}</button> },
            ]}
          />
        )}
      </section>
    </div>
  );
}
